import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchServices } from '../services/serviceplans';
import type { Service } from '../types/services.types';

interface UseServicesQueryParams {
    product_id: string;
    make_id?: string;
    model_id?: string;
    derivative_id?: string;
}

export const serviceKeys = {
    all: ['services'] as const,
    lists: () => [...serviceKeys.all, 'list'] as const,
    list: (filters: UseServicesQueryParams) => [...serviceKeys.lists(), filters] as const,
};

export const useServicesQuery = (
    params: UseServicesQueryParams = {
        product_id: '',
    }
) => {
    const query = useQuery({
        queryKey: serviceKeys.list(params),
        queryFn: async () => {
            const services: Service[] = await fetchServices(params);
            return services;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 2,
        // Only fire once a real product id is present. When `selectedProduct`
        // is missing the caller builds `{ product_id: '' }`, and `fetchServices`
        // interpolates the id straight into the URL path — an empty id would
        // request `/smr/products//services`, a guaranteed-invalid call that
        // only produces an avoidable error state.
        enabled: !!params.product_id,
    });

    useEffect(() => {
        if (query.isError) {
            console.error('Failed to fetch products:', query.error);
        }
    }, [query.isError, query.error]);

    return query;
};

export const useAvailableServices = (
    params: UseServicesQueryParams = {
        product_id: '',
    }
) => {
    const { data: services = [], isLoading, error } = useServicesQuery(params);

    // Stable identity across renders so consumers can include this in a
    // `useEffect` dependency array without refiring.
    const getServiceById = useCallback(
        (id: string): Service | undefined => services.find((service) => service.id === id),
        [services]
    );

    return {
        services,
        getServiceById,
        isLoading,
        error,
    };
};
